<script setup>
import {computed, defineExpose, defineModel, defineProps, nextTick, ref, watch} from 'vue'
import {useNotification} from '@kyvg/vue3-notification'
import {isFacebookUrl} from '@shared/utils/helpers'
import TheSelectMetaAdAccounts from '@shared-components/ui/selects/TheSelectMetaAdAccounts.vue'
import TheSelectBusinessManagers from '@/views/assets/business-managers/components/TheSelectBusinessManagers.vue'
import ButtonGroup from '@shared-components/ui/buttons/ButtonGroup.vue'
import BaseCheckbox from '@shared-components/base/BaseCheckbox.vue'
import InputPreloader from '@shared-components/ui/inputs/InputPreloader.vue'
import FacebookUrlListInput from '@shared-components/ui/inputs/FacebookUrlListInput.vue'
import {useMetaAdAccountStore} from '@/views/assets/ad-accounts/stores/MetaAdAccountStore'

const {notify} = useNotification()
const {getFacebookAdAccount} = useMetaAdAccountStore()

const modelValue = defineModel({required: true})
const isValid = defineModel('isValid', {type: Boolean, default: true})

const props = defineProps({
  isCreating: {
    type: Boolean,
    default: true,
  },
  companyHash: {
    type: String,
    default: '',
  },
  pageAccess: {
    type: Object,
    default: null,
  },
  adAccount: {
    type: Object,
    default: null,
  },
  legacyPages: {
    type: String,
    default: '',
  },
  legacyProfiles: {
    type: String,
    default: '',
  },
})

const pagesModeOptions = [
  {label: 'Enter Facebook Page URLs', value: 'manual'},
  {label: 'All Facebook Pages', value: 'all'},
]
const accessTypeOptions = [
  {label: 'Facebook Business Manager', value: 'business_manager'},
  {label: 'Facebook Profile', value: 'profile'},
]

// Refs
const selectAdAccounts = ref()
const selectBusinessManagers = ref()
const selectedAdAccountId = ref(null)
const availablePages = ref([])
const pagesLoading = ref(false)
const pagesLoaded = ref(false)

// Computed
const hasAvailablePages = computed(() => availablePages.value.length > 0)
const isSelectedPagesMode = computed(() => modelValue.value.pages_mode === 'selected')
const isManualPagesMode = computed(() => modelValue.value.pages_mode === 'manual')
const isBusinessManagerAccess = computed(() => modelValue.value.access_type === 'business_manager')
const isProfileAccess = computed(() => modelValue.value.access_type === 'profile')
const isAllPagesSelected = computed(() => modelValue.value.page_hashes.length === availablePages.value.length)
const isNoPagesSelected = computed(() => modelValue.value.page_hashes.length === 0)
const showPagesSection = computed(() => props.isCreating && pagesLoaded.value && !pagesLoading.value)
const isLegacyView = computed(() => !props.isCreating && !props.pageAccess)
const viewPageAccess = computed(() => props.pageAccess ?? {})
const hasValidUrl = urls => urls.some(url => isFacebookUrl(url))
const isPagesStepComplete = computed(() => {
  if (isSelectedPagesMode.value) {
    return !isNoPagesSelected.value
  }

  if (isManualPagesMode.value) {
    return hasValidUrl(modelValue.value.page_urls)
  }

  return modelValue.value.pages_mode === 'all'
})
const isAccessStepComplete = computed(() => {
  if (isBusinessManagerAccess.value) {
    return !!modelValue.value.business_manager_hash
  }

  if (isProfileAccess.value) {
    return hasValidUrl(modelValue.value.profile_urls)
  }

  return false
})
const isFormComplete = computed(
  () =>
    modelValue.value.ad_account_ids.length > 0 &&
    showPagesSection.value &&
    isPagesStepComplete.value &&
    isAccessStepComplete.value,
)
const viewAdAccountTitle = computed(() => {
  if (!props.adAccount) {
    return ''
  }

  const externalId = props.adAccount.clean_external_id ?? props.adAccount.external_id

  return externalId ? `${props.adAccount.title} (${externalId})` : props.adAccount.title
})
const viewBusinessManagerTitle = computed(() => {
  const businessManager = viewPageAccess.value.business_manager

  if (!businessManager) {
    return ''
  }

  return businessManager.external_id
    ? `${businessManager.title} (ID: ${businessManager.external_id})`
    : businessManager.title
})

// Methods
const resetPages = () => {
  availablePages.value = []
  pagesLoaded.value = false
  modelValue.value.pages_mode = ''
  modelValue.value.page_hashes = []
  modelValue.value.page_urls = ['']
}

const resetAdAccount = () => {
  selectedAdAccountId.value = null
  modelValue.value.ad_account_ids = []
  resetPages()
}

const loadAdAccountPages = async adAccount => {
  pagesLoading.value = true

  try {
    const response = await getFacebookAdAccount(adAccount.external_id, {with: ['pages']})

    availablePages.value = response?.pages ?? []
    modelValue.value.pages_mode = hasAvailablePages.value ? 'selected' : ''
    modelValue.value.page_hashes = availablePages.value.map(page => page.hash)
  } catch (responseError) {
    notify({type: 'error', text: responseError?.data?.message ?? 'Failed to load Facebook Pages'})
    availablePages.value = []
    modelValue.value.pages_mode = ''
    modelValue.value.page_hashes = []
  } finally {
    pagesLoaded.value = true
    pagesLoading.value = false
  }
}

const onAdAccountChange = async adAccount => {
  resetPages()

  if (!adAccount) {
    modelValue.value.ad_account_ids = []

    return
  }

  modelValue.value.ad_account_ids = [adAccount.id]

  await loadAdAccountPages(adAccount)
}

const onPageToggle = (pageHash, checked) => {
  const pageHashes = modelValue.value.page_hashes.filter(hash => hash !== pageHash)

  modelValue.value.page_hashes = checked ? [...pageHashes, pageHash] : pageHashes
}

const selectAllPages = () => {
  modelValue.value.page_hashes = availablePages.value.map(page => page.hash)
}

const unselectAllPages = () => {
  modelValue.value.page_hashes = []
}

const onAccessTypeChange = () => {
  modelValue.value.business_manager_hash = undefined
  modelValue.value.profile_urls = ['']

  if (isBusinessManagerAccess.value) {
    nextTick(() => {
      selectBusinessManagers.value?.handleSearch('  ')
    })
  }
}

const getPayload = () => {
  const payload = cloneSafe(modelValue.value)

  payload.page_hashes = isSelectedPagesMode.value ? payload.page_hashes : []
  payload.page_urls = isManualPagesMode.value ? payload.page_urls.filter(url => url.trim() !== '') : []
  payload.profile_urls = isProfileAccess.value ? payload.profile_urls.filter(url => url.trim() !== '') : []

  if (!isBusinessManagerAccess.value) {
    delete payload.business_manager_hash
  }

  return payload
}

watch(
  () => props.companyHash,
  () => {
    if (!props.isCreating) {
      return
    }

    resetAdAccount()
    modelValue.value.access_type = ''
    modelValue.value.business_manager_hash = undefined

    if (props.companyHash) {
      nextTick(() => {
        selectAdAccounts.value?.requestGetAdAccounts()
      })
    }
  },
)

watch(
  () => (props.isCreating ? isFormComplete.value : true),
  value => {
    isValid.value = value
  },
  {immediate: true},
)

defineExpose({getPayload})
</script>

<template>
  <template v-if="isLegacyView">
    <div class="form-group">
      <label for="other-request-page-access-legacy-pages">FB Pages to Share</label>
      <textarea
        id="other-request-page-access-legacy-pages"
        class="form-control"
        :value="legacyPages"
        rows="3"
        disabled
      ></textarea>
    </div>
    <div class="form-group">
      <label for="other-request-page-access-legacy-profiles">Link to Facebook Profile to Share Access</label>
      <textarea
        id="other-request-page-access-legacy-profiles"
        class="form-control"
        :value="legacyProfiles"
        rows="3"
        disabled
      ></textarea>
    </div>
  </template>

  <div v-if="!isLegacyView" class="form-group">
    <label for="other-request-page-access-ad-account">Ad Account *</label>
    <template v-if="isCreating">
      <the-select-meta-ad-accounts
        v-model="selectedAdAccountId"
        :payload="{company_hash: companyHash}"
        :load-on-init="!!companyHash"
        :disabled="!companyHash"
        placeholder="Select Ad Account"
        ref="selectAdAccounts"
        @changed="onAdAccountChange"
        required
      />
    </template>
    <input v-else class="form-control" id="other-request-page-access-ad-account" :value="viewAdAccountTitle" disabled />
  </div>

  <template v-if="isCreating">
    <input-preloader v-if="pagesLoading" :show-preloader="true" name="Facebook Pages">
      <input class="form-control" />
    </input-preloader>

    <template v-if="showPagesSection">
      <div v-if="hasAvailablePages" class="form-group">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <label class="mb-0">Facebook Pages *</label>
          <div class="btn-group btn-row-group" role="group" aria-label="Facebook Pages selection">
            <button
              :class="['btn btn-secondary btn-sm', {active: isAllPagesSelected}]"
              type="button"
              @click="selectAllPages"
            >
              Select All
            </button>
            <button
              :class="['btn btn-secondary btn-sm', {active: isNoPagesSelected}]"
              type="button"
              @click="unselectAllPages"
            >
              Unselect All
            </button>
          </div>
        </div>
        <input v-if="isNoPagesSelected" class="d-none" required />
        <div class="facebook-pages-list">
          <div v-for="page in availablePages" :key="page.hash" class="facebook-pages-list__item">
            <base-checkbox
              :id="`other-request-page-access-page-${page.hash}`"
              :model-value="modelValue.page_hashes.includes(page.hash)"
              :title="page.name || page.url"
              @update:model-value="onPageToggle(page.hash, $event)"
            />
            <a
              v-if="page.name"
              :href="page.url"
              class="small text-muted text-ellipsis"
              target="_blank"
              rel="nofollow noopener noreferrer"
              >{{ page.url }}</a
            >
          </div>
        </div>
        <small class="text-muted">Uncheck the Pages you do not want to share access to.</small>
      </div>

      <template v-else>
        <div class="form-group">
          <label>Facebook Pages *</label>
          <div class="alert alert-warning alert-outline-warning mb-2">
            No Facebook Pages are linked to this Ad Account yet. Enter the Page URLs or request access to all Pages.
          </div>
          <input v-if="!modelValue.pages_mode" class="d-none" required />
          <button-group v-model="modelValue.pages_mode" :options="pagesModeOptions" id="other-request-pages-mode" />
        </div>
        <div v-if="isManualPagesMode" class="form-group">
          <label>Facebook Page URLs *</label>
          <facebook-url-list-input
            v-model="modelValue.page_urls"
            id="other-request-page-access-page-url"
            placeholder="https://www.facebook.com/your_page"
            hint="Enter the Facebook Page links. Click '+' to include additional Pages."
            invalid-message="Enter a valid Facebook Page link (facebook.com)"
          />
        </div>
      </template>
    </template>

    <div v-if="showPagesSection" class="form-group">
      <label>Who Gets Access *</label>
      <input v-if="!modelValue.access_type" class="d-none" required />
      <button-group
        v-model="modelValue.access_type"
        :options="accessTypeOptions"
        id="other-request-access-type"
        @update:model-value="onAccessTypeChange"
      />
    </div>

    <div v-if="showPagesSection && isBusinessManagerAccess" class="form-group">
      <label>Facebook Business Manager *</label>
      <the-select-business-managers
        v-model="modelValue.business_manager_hash"
        :list-by-search="true"
        platform="meta"
        :payload="{company_hash: companyHash}"
        :disabled="!companyHash"
        :with-appended="false"
        placeholder="Select Business Manager"
        mode="single"
        ref="selectBusinessManagers"
        required
      />
    </div>

    <div v-if="showPagesSection && isProfileAccess" class="form-group">
      <label>Facebook Profile Links *</label>
      <facebook-url-list-input
        v-model="modelValue.profile_urls"
        id="other-request-page-access-profile-url"
        placeholder="https://www.facebook.com/profile.php?id=..."
        hint="Enter the Facebook Profile links that should get access. Click '+' to include additional profiles."
        invalid-message="Enter a valid Facebook Profile link (facebook.com)"
      />
    </div>
  </template>

  <template v-else-if="!isLegacyView">
    <div class="form-group">
      <label>Facebook Pages</label>
      <input v-if="viewPageAccess.pages_mode === 'all'" class="form-control" value="All Facebook Pages" disabled />
      <facebook-url-list-input
        v-else-if="viewPageAccess.pages_mode === 'manual'"
        :model-value="viewPageAccess.page_urls ?? []"
        id="other-request-page-access-page-url"
        readonly
      />
      <div v-else class="facebook-pages-list">
        <div v-for="page in viewPageAccess.pages ?? []" :key="page.hash" class="facebook-pages-list__item">
          <div>{{ page.name || page.url }}</div>
          <a
            v-if="page.name"
            :href="page.url"
            class="small text-muted text-ellipsis"
            target="_blank"
            rel="nofollow noopener noreferrer"
            >{{ page.url }}</a
          >
        </div>
      </div>
    </div>

    <div class="form-group">
      <label>Who Gets Access</label>
      <button-group
        :model-value="viewPageAccess.access_type"
        :options="accessTypeOptions"
        id="other-request-access-type"
        disabled
      />
    </div>

    <div v-if="viewPageAccess.access_type === 'business_manager'" class="form-group">
      <label>Facebook Business Manager</label>
      <input class="form-control" :value="viewBusinessManagerTitle" disabled />
    </div>

    <div v-if="viewPageAccess.access_type === 'profile'" class="form-group">
      <label>Facebook Profile Links</label>
      <facebook-url-list-input
        :model-value="viewPageAccess.profile_urls ?? []"
        id="other-request-page-access-profile-url"
        readonly
      />
    </div>
  </template>
</template>

<style lang="scss" scoped>
.facebook-pages-list {
  display: flex;
  flex-direction: column;
  gap: toRem(8);
  max-height: toRem(260);
  overflow-y: auto;
  padding: toRem(12);
  border: 1px solid $secondary-grey;
  border-radius: toRem(6);

  &__item {
    display: flex;
    flex-direction: column;
    gap: toRem(2);
  }
}
</style>
